import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Platform,
  Pressable,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { ChevronRight, Scan } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import { SupportingSubtitleText } from "@louise/moon-ui/primitives/SupportingSubtitleText";
import { FieldMessage } from "@louise/moon-ui/primitives/FieldMessage";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type SelectedFile = {
  name: string;
  uri: string;
  mimeType: string;
  size?: number;
  objectUrl?: string;
};

type WebDropEvent = DragEvent;
type WebDropZoneTarget = View & {
  addEventListener: HTMLElement["addEventListener"];
  removeEventListener: HTMLElement["removeEventListener"];
};

export function createSelectedFileFromWebFile(file: File): SelectedFile {
  const objectUrl = URL.createObjectURL(file);
  return {
    name: file.name || "Document",
    uri: objectUrl,
    mimeType: resolveUploadContentType({
      name: file.name,
      uri: objectUrl,
      mimeType: file.type,
    }),
    size: file.size,
    objectUrl,
  };
}

function isWebDropZoneTarget(node: View | null): node is WebDropZoneTarget {
  if (!node || typeof node !== "object") return false;
  return (
    "addEventListener" in node &&
    typeof node.addEventListener === "function" &&
    "removeEventListener" in node &&
    typeof node.removeEventListener === "function"
  );
}

function getDroppedFiles(event: WebDropEvent) {
  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) return [];
  return Array.from(files);
}

export function DocumentFileDropZone({
  onPick,
  onFileSelected,
  onFilesSelected,
  compact = false,
  primaryLabel,
  secondaryLabel,
  action = "documents.upload.choose_file",
  children,
  dropHint,
  singleFile = false,
}: {
  onPick: () => void;
  onFileSelected?: (file: SelectedFile) => void;
  onFilesSelected?: (files: SelectedFile[]) => void;
  compact?: boolean;
  primaryLabel?: string;
  secondaryLabel?: string;
  /**
   * Stable analytics identifier for a press. Defaults to the shared
   * choose-file id; pass one where a funnel already groups on its own.
   */
  action?: string;
  /** Illustrated content owns its picker button; the surrounding target is not a button. */
  children?: ReactNode;
  /** Desktop-only guidance beneath illustrated content. */
  dropHint?: string;
  /** A review flow that can accept only one report must not discard extra files. */
  singleFile?: boolean;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("DocumentFileDropZone");
  const [isDragging, setIsDragging] = useState(false);
  const [tooManyFiles, setTooManyFiles] = useState(false);
  const dropZoneRef = useRef<View | null>(null);
  const { width } = useWindowDimensions();
  const showDropTarget = Platform.OS === "web" && width >= 768;
  const hasCustomContent = Boolean(children);
  const helperLabel =
    secondaryLabel ??
    (compact
      ? t("documents.upload.choose_file")
      : t("documents.upload.file_hint"));
  const handleFilesSelected = useCallback(
    (files: SelectedFile[]) => {
      if (files.length === 0) return;
      if (onFilesSelected) {
        onFilesSelected(files);
        return;
      }
      onFileSelected?.(files[0]!);
    },
    [onFileSelected, onFilesSelected],
  );

  useEffect(() => {
    if (Platform.OS !== "web" || (hasCustomContent && !showDropTarget)) return;

    const node = dropZoneRef.current;
    if (!isWebDropZoneTarget(node)) return;

    const handleDragEnter = (event: WebDropEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      setIsDragging(true);
    };
    const handleDragOver = (event: WebDropEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    };
    const handleDragLeave = (event: WebDropEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      // Entering a nested icon or button does not mean leaving the target.
      if (
        hasCustomContent &&
        event.currentTarget instanceof HTMLElement &&
        event.relatedTarget instanceof Node &&
        event.currentTarget.contains(event.relatedTarget)
      ) {
        return;
      }
      setIsDragging(false);
    };
    const handleDrop = (event: WebDropEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      setIsDragging(false);
      const droppedFiles = getDroppedFiles(event);
      if (singleFile && droppedFiles.length > 1) {
        setTooManyFiles(true);
        return;
      }
      setTooManyFiles(false);
      const files = droppedFiles.map(createSelectedFileFromWebFile);
      handleFilesSelected(files);
    };

    node.addEventListener("dragenter", handleDragEnter);
    node.addEventListener("dragover", handleDragOver);
    node.addEventListener("dragleave", handleDragLeave);
    node.addEventListener("drop", handleDrop);

    return () => {
      node.removeEventListener("dragenter", handleDragEnter);
      node.removeEventListener("dragover", handleDragOver);
      node.removeEventListener("dragleave", handleDragLeave);
      node.removeEventListener("drop", handleDrop);
    };
  }, [hasCustomContent, handleFilesSelected, showDropTarget, singleFile]);

  if (children) {
    return (
      <View
        ref={dropZoneRef}
        testID="document-upload-drop-target"
        style={{
          width: "100%",
          alignItems: "center",
          gap: 12,
          borderWidth: showDropTarget ? 2 : 0,
          borderStyle: "dashed",
          borderColor: isDragging
            ? semanticColors.border.focus
            : `${semanticColors.text.primary}40`,
          borderRadius: 14,
          padding: showDropTarget ? 24 : 0,
          backgroundColor: showDropTarget
            ? isDragging
              ? semanticColors.surface.subtle
              : semanticColors.surface.glass
            : "transparent",
        }}
      >
        {children}
        {showDropTarget && dropHint ? (
          <SupportingSubtitleText style={{ textAlign: "center", marginTop: 0 }}>
            {dropHint}
          </SupportingSubtitleText>
        ) : null}
        {tooManyFiles ? (
          <FieldMessage
            tone="info"
            centered
            testID="upload-single-file-guidance"
          >
            {t("documents.upload.choose_one_file_at_a_time")}
          </FieldMessage>
        ) : null}
      </View>
    );
  }

  return (
    <Pressable
      ref={dropZoneRef}
      onPress={() => {
        capturePress(
          pressLabel(
            primaryLabel ??
              (compact
                ? t("homepage.test_result_document.upload_action")
                : t("documents.upload.choose_file")),
          ),
          action,
        );
        onPick();
      }}
      accessibilityRole="button"
      style={{
        flexDirection: compact ? "row" : "column",
        borderWidth: 1.5,
        borderColor: isDragging
          ? "rgba(122, 62, 21, 0.42)"
          : "rgba(122, 62, 21, 0.18)",
        borderStyle: "dashed",
        borderRadius: 12,
        paddingHorizontal: compact ? 12 : 0,
        paddingVertical: compact ? 10 : 28,
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        backgroundColor: isDragging
          ? "rgba(122, 62, 21, 0.06)"
          : "rgba(0,0,0,0.02)",
      }}
    >
      <Scan
        size={compact ? 20 : 28}
        color={EMBER.colors.warm}
        strokeWidth={1.5}
      />
      <View
        style={{
          flex: compact ? 1 : undefined,
          gap: compact ? 2 : 10,
          alignItems: compact ? "flex-start" : "center",
        }}
      >
        <Text
          className="font-dm-sans"
          numberOfLines={compact ? 1 : undefined}
          style={{
            fontSize: compact ? 13 : 14,
            fontWeight: "500",
            color: EMBER.colors.text,
            textAlign: compact ? "left" : "center",
          }}
        >
          {primaryLabel ??
            (compact
              ? t("homepage.test_result_document.upload_action")
              : t("documents.upload.choose_file"))}
        </Text>
        {helperLabel ? (
          <Text
            className="font-dm-sans"
            numberOfLines={compact ? 1 : undefined}
            style={{
              fontSize: compact ? 11 : 12,
              color: EMBER.colors.textFaint,
              textAlign: compact ? "left" : "center",
            }}
          >
            {helperLabel}
          </Text>
        ) : null}
      </View>
      {compact ? (
        <ChevronRight
          size={18}
          color={EMBER.colors.textFaint}
          strokeWidth={1.5}
        />
      ) : null}
    </Pressable>
  );
}
